// @ts-nocheck
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { sx } from '../sx';
import InfoTip from './InfoTip';

const LABEL = `display:flex;align-items:center;gap:6px;font-size:12.5px;font-weight:500;color:var(--ink);margin-bottom:8px`;
const HINT = `font-size:11.5px;font-weight:400;color:var(--ink-4);margin:8px 0 0;line-height:1.5`;
const INPUT = `width:100%;height:38px;padding:0 12px;border:1px solid #C8D4EA;border-radius:8px;font-family:inherit;font-size:12.5px;color:var(--ink);background:#fff;outline:none;box-sizing:border-box`;
const READ = `display:flex;align-items:center;min-height:38px;padding:0 12px;border:1px solid #EDF1F8;border-radius:8px;background:var(--bg-1);font-size:12.5px;color:var(--ink-2);box-sizing:border-box;word-break:break-all`;

function Chips({ items, busy, nowrap = false }) {
  return (
    <div style={sx(`display:flex;gap:6px;flex-wrap:${nowrap ? 'nowrap' : 'wrap'}`)}>
      {(items || []).map((o, i) => {
        const off = !!o.disabled || busy;
        return (
          <button
            key={o.id || i}
            onClick={o.onClick}
            disabled={off}
            style={sx(`flex:none;height:30px;padding:0 13px;border:1px solid ${o.border};border-radius:999px;background:${o.bg};color:${o.fg};font-family:inherit;font-size:12px;font-weight:${o.fw || 400};cursor:${busy ? 'wait' : off ? 'not-allowed' : 'pointer'};opacity:${off ? 0.65 : 1}`)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Label({ text, info }) {
  return (
    <span style={sx(LABEL)}>
      {text}
      <InfoTip text={info} label={text} />
    </span>
  );
}

function Field({ label, info, children }) {
  return (
    <div>
      <Label text={label} info={info} />
      {children}
    </div>
  );
}

function Section({ title, info, summary, open, onToggle, children }) {
  return (
    <div style={sx(`border:1px solid #DCE4F2;border-radius:12px`)}>
      <div style={sx(`display:flex;align-items:center;gap:8px;padding-right:14px`)}>
        <button
          onClick={onToggle}
          aria-expanded={open}
          style={sx(`flex:1;min-width:0;display:flex;align-items:center;gap:10px;padding:12px 0 12px 14px;border:none;background:none;font-family:inherit;text-align:left;cursor:pointer`)}
        >
          <span style={sx(`flex:none;font-size:10px;color:var(--ink-4);transform:rotate(${open ? 90 : 0}deg);transition:transform 0.15s`)}>▶</span>
          <span style={sx(`flex:none;font-size:12.5px;font-weight:500;color:var(--ink)`)}>{title}</span>
          {summary ? <span style={sx(`flex:1;min-width:0;font-size:11.5px;color:var(--ink-4);overflow:hidden;text-overflow:ellipsis;white-space:nowrap`)}>{summary}</span> : null}
        </button>
        <InfoTip text={info} label={title} />
      </div>
      {open ? <div style={sx(`display:flex;flex-direction:column;gap:18px;padding:4px 14px 16px`)}>{children}</div> : null}
    </div>
  );
}

function ReadOnly({ label, info, value }) {
  return (
    <Field label={label} info={info}>
      <div style={sx(READ)}>{value}</div>
    </Field>
  );
}

function CheckItem({ c, first = false }) {
  return (
    <div style={sx(`display:flex;gap:12px;padding:12px 0;border-top:${first ? 'none' : '1px solid #EDF1F8'}`)}>
      <span style={sx(`flex:none;width:4px;border-radius:999px;background:${c.bar}`)}></span>
      <div style={sx(`flex:1;min-width:0`)}>
        <div style={sx(`display:flex;align-items:center;gap:8px`)}>
          <span style={sx(`font-size:10.5px;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;color:var(--ink-4)`)}>{c.code}</span>
          <span style={sx(`font-size:10px;font-weight:500;letter-spacing:0.08em;padding:2px 6px;border-radius:4px;background:${c.bg};color:${c.fg}`)}>{c.status}</span>
        </div>
        <div style={sx(`font-size:12.5px;color:var(--ink);margin-top:5px;line-height:1.45`)}>{c.title}</div>
        <div style={sx(`font-size:11.5px;color:var(--ink-4);margin-top:4px;line-height:1.5;word-break:break-word`)}>{c.detail}</div>
        {c.status !== 'PASS' && (c.fixes || []).length ? (
          <div style={sx(`display:flex;gap:6px;flex-wrap:wrap;margin-top:8px`)}>
            {c.fixes.map((f) => (
              <button
                key={f.token + f.key}
                className="gmHover"
                onClick={f.onClick}
                title={'Replace ' + f.token + ' with ' + f.key}
                style={sx(`flex:none;display:flex;align-items:baseline;gap:6px;height:28px;padding:0 12px;border:1px solid #C8D4EA;border-radius:999px;background:#fff;font-family:inherit;font-size:12px;color:#0052CD;cursor:pointer`)}
                data-hover="border-color:#0052CD"
              >
                {f.key}
                <span style={sx(`font-size:11px;color:var(--ink-4)`)}>{f.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function selectedLabel(items) {
  const hit = (items || []).find((o) => o.bg === '#0052CD');
  return hit ? hit.label : '—';
}

function AuthoringDialog({ vm }) {
  const [lookOpen, setLookOpen] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const busy = !!vm.draftSaving;
  const checks = vm.checks || [];
  const failed = checks.filter((c) => c.status !== 'PASS');
  const passed = checks.filter((c) => c.status === 'PASS');
  const checksSummary = failed.length
    ? `${failed.length} to fix · ${passed.length} of ${checks.length} passed`
    : `All ${checks.length} checks passed`;

  return (
    <div style={sx(`position:fixed;inset:0;z-index:60;background:rgba(8,35,76,0.45);display:flex;align-items:center;justify-content:center;padding:24px`)}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={vm.authTitle}
        style={sx(`position:relative;width:100%;max-width:760px;max-height:calc(100vh - 48px);display:flex;flex-direction:column;overflow:hidden;background:#fff;border-radius:24px;box-shadow:0 30px 60px -25px rgba(8,35,76,0.5)`)}
      >
        <button
          type="button"
          className="gmHover"
          onClick={vm.closeAuth}
          disabled={busy}
          aria-label="Close"
          title="Close"
          style={sx(`position:absolute;top:16px;right:16px;z-index:1;display:flex;align-items:center;justify-content:center;width:32px;height:32px;border:1px solid #DCE4F2;border-radius:999px;background:#fff;color:var(--ink-3);cursor:${busy ? 'wait' : 'pointer'};opacity:${busy ? 0.6 : 1}`)}
          data-hover="border-color:#0052CD;color:#0052CD"
        >
          <X size={16} strokeWidth={1.75} />
        </button>
        <div style={sx(`flex:1;min-height:0;overflow:auto`)}>
          <div style={sx(`padding:24px 64px 8px 24px`)}>
            <div style={sx(`font-size:11px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-4)`)}>{vm.authTitle}</div>
            <div style={sx(`display:flex;align-items:center;gap:8px;margin:6px 0 4px`)}>
              <h4 style={sx(`margin:0`)}>{vm.auth.name}</h4>
              <InfoTip text={vm.authIntro} />
            </div>
            <p style={sx(`font-size:11.5px;font-weight:400;color:#0060a8;margin:0`)}>{vm.authPendingNote}</p>
          </div>

          <div style={sx(`display:flex;flex-direction:column;gap:20px;padding:16px 24px 24px`)}>
            <label style={sx(`display:block`)}>
              <Label text="Driver key" info="Used to refer to this driver in formulas. Renaming it updates every formula that uses it." />
              <input className="gmFocus" value={vm.auth.key} onChange={vm.authKeyChange} disabled={busy} style={sx(INPUT)} />
            </label>

            <label style={sx(`display:block`)}>
              <Label text="Display name" info="The name shown on the plan and in the model structure." />
              <input className="gmFocus" value={vm.auth.name} onChange={vm.authNameChange} disabled={busy} style={sx(INPUT)} />
            </label>

            {vm.authManual ? (
              <div>
                <Label text="Actuals" info={vm.authManualNote} />
                <div aria-label="Actuals" style={sx(READ)}>Manual · {vm.authManualNote}</div>
              </div>
            ) : vm.authCombined && !vm.isFormula ? null : (
              <div>
                <Label
                  text="Formula or binding"
                  info={`${vm.isFormula ? `${vm.authFormulaNote} ` : 'Choose FORMULA to write your own formula. '}The binding describes where the actual values come from. ${vm.authBindingNote || ''}`.trim()}
                />
                <div style={sx(`display:flex;flex-direction:column;gap:8px`)}>
                  {vm.isFormula ? (
                    <input
                      className="gmFocus"
                      aria-label="Formula"
                      value={vm.auth.formula}
                      onChange={vm.authFormulaChange}
                      disabled={busy}
                      placeholder={vm.authFormulaExample}
                      style={sx(`${INPUT};height:44px;padding:0 14px;font-size:13px;background:var(--bg-1)`)}
                    />
                  ) : vm.auth.formula ? (
                    <div aria-label="Formula" style={sx(READ)}>{vm.auth.formula}</div>
                  ) : null}
                  {vm.authCombined ? null : (
                    <input
                      className="gmFocus"
                      aria-label="Binding"
                      value={vm.auth.binding || ''}
                      onChange={vm.authBindingChange}
                      disabled={busy}
                      maxLength={500}
                      placeholder={vm.isFormula ? 'Actuals key (optional), e.g. sales_quantity' : 'Actuals key, e.g. sales_quantity'}
                      style={sx(INPUT)}
                    />
                  )}
                </div>
                {vm.authBindingNote ? (
                  <p style={sx(`${HINT};color:${vm.authBindingBound ? 'var(--ink-2)' : '#E5484D'}`)}>{vm.authBindingNote}</p>
                ) : null}
                {vm.authFormulaReadable ? (
                  <p style={sx(`${HINT};color:var(--ink-2)`)}>Reads as: {vm.auth.name} = {vm.authFormulaReadable}</p>
                ) : null}
              </div>
            )}

            <Field
              label="Plan Source"
              info={vm.isBinding ? `${vm.authSrcNote} Reads ${vm.auth.key} for ${vm.channelName}, month by month up to ${vm.cutoffLabel}.` : vm.authSrcNote}
            >
              <Chips items={vm.srcSeg} busy={busy} />
            </Field>

            {vm.isRollupRule ? (
              <label style={sx(`display:block`)}>
                <Label text="Rollup rule" info={vm.authRollupNote} />
                <textarea
                  className="gmFocus"
                  aria-label="Rollup rule"
                  value={vm.auth.rollup || ''}
                  onChange={vm.authRollupChange}
                  disabled={busy}
                  maxLength={1000}
                  rows={2}
                  placeholder="e.g. SUM(ad_spend) / SUM(new_customers)"
                  style={sx(`${INPUT};height:auto;min-height:60px;padding:10px 12px;resize:vertical;line-height:1.5`)}
                />
                {vm.authRollupReadable ? (
                  <p style={sx(`${HINT};color:var(--ink-2)`)}>Reads as: {vm.auth.name} = {vm.authRollupReadable}</p>
                ) : null}
              </label>
            ) : null}

            {vm.isOverwrite ? (
              <label style={sx(`display:block`)}>
                <Label
                  text="Overwrite formula"
                  info="Optional. Recalculates this input for the actual months up to the cutoff. Use INPUT for this driver's own value and combine it with other driver keys. Forecast months keep the typed values. Leave empty to use the input as it is."
                />
                <input
                  className="gmFocus"
                  aria-label="Overwrite formula"
                  value={vm.auth.overwrite || ''}
                  onChange={vm.authOverwriteChange}
                  disabled={busy}
                  maxLength={1000}
                  placeholder="e.g. INPUT - (INPUT / (ad_spend * ad_spend_manual_total))"
                  style={sx(INPUT)}
                />
              </label>
            ) : null}

            {(vm.authUsedIn || []).length ? (
              <div style={sx(`padding:12px 14px;border-radius:10px;background:var(--bg-1);border:1px solid #DCE4F2`)}>
                <div style={sx(`font-size:12px;font-weight:500;color:var(--ink);margin-bottom:6px`)}>Used in</div>
                {vm.authUsedIn.map((u) => (
                  <div key={u.name} style={sx(`font-size:12px;color:var(--ink-2);line-height:1.6;word-break:break-word`)}>{u.formula}</div>
                ))}
              </div>
            ) : null}

            <Field label="Where does it show on the plan?" info={vm.authSecNote}>
              <Chips items={vm.secSeg} busy={busy} />
            </Field>

            <Field label="What kind of number is it?">
              <Chips items={vm.unitSeg} busy={busy} />
            </Field>

            {vm.authShapeLocked ? (
              <div style={sx(`display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto;gap:16px;align-items:end`)}>
                <ReadOnly label="Split by" info={`${vm.authShape}. Built-in drivers keep the shape the model defines.`} value={(vm.dimChips || []).filter((o) => o.bg === '#0052CD').map((o) => o.label).join(', ') || 'Whole business'} />
                <ReadOnly label="Time" value={selectedLabel(vm.tmpSeg)} />
                <Field label="Quarter and year totals" info={vm.authTotalsNote}>
                  <div style={sx(`display:flex;align-items:center;min-height:38px`)}>
                    <Chips items={vm.totalsSeg} busy={busy} nowrap />
                  </div>
                </Field>
              </div>
            ) : (
              <>
                <Field label="Split the number by" info="Leave all off for one value that applies to the whole business.">
                  <Chips items={vm.dimChips} busy={busy} />
                </Field>
                <div style={sx(`display:flex;flex-wrap:wrap;gap:20px 32px`)}>
                  <Field label="Time">
                    <Chips items={vm.tmpSeg} busy={busy} />
                  </Field>
                  <Field label="Quarter and year totals" info={vm.authTotalsNote}>
                    <Chips items={vm.totalsSeg} busy={busy} nowrap />
                  </Field>
                </div>
              </>
            )}

            {vm.isTotalsFormula ? (
              <label style={sx(`display:block`)}>
                <Label text="Quarter and year totals formula" info="Worked out on the quarter and year totals of the drivers it refers to, each using its own setting. A No totals driver counts as the sum of its months. Use driver keys with + - * / and brackets." />
                <input
                  className="gmFocus"
                  aria-label="Quarter and year totals formula"
                  value={vm.auth.totalsFormula || ''}
                  onChange={vm.authTotalsFormulaChange}
                  disabled={busy}
                  placeholder={vm.authFormulaExample}
                  style={sx(INPUT)}
                />
                {vm.authTotalsFormulaReadable ? (
                  <p style={sx(`${HINT};color:var(--ink-2)`)}>Reads as: {vm.auth.name} = {vm.authTotalsFormulaReadable}</p>
                ) : null}
              </label>
            ) : null}

            {vm.authLever ? (
              <Field label="Scenario panel" info="Off takes this driver's slider out of the scenario panel and clears any scenario change on it.">
                <button
                  role="switch"
                  aria-checked={vm.authScenOn}
                  aria-label="Show in scenario panel"
                  onClick={vm.toggleAuthScen}
                  disabled={busy}
                  style={sx(`display:flex;align-items:center;gap:8px;height:28px;padding:0;border:none;background:none;font-family:inherit;font-size:12.5px;color:${vm.authScenOn ? '#0052CD' : 'var(--ink-4)'};cursor:pointer`)}
                >
                  <span style={sx(`position:relative;width:34px;height:20px;border-radius:999px;background:${vm.authScenOn ? '#0052CD' : '#C8D4EA'};transition:background 0.15s`)}>
                    <span style={sx(`position:absolute;top:2px;left:${vm.authScenOn ? '16px' : '2px'};width:16px;height:16px;border-radius:50%;background:#fff;box-shadow:0 1px 2px rgba(8,35,76,0.25);transition:left 0.15s`)}></span>
                  </span>
                  {vm.authScenOn ? 'On' : 'Off'}
                </button>
              </Field>
            ) : null}

            {failed.length ? (
              <div role="alert" style={sx(`padding:4px 14px;border:1px solid #F5C2C4;border-radius:12px;background:#FFF7F7`)}>
                {failed.map((c, i) => (
                  <CheckItem key={i} c={c} first={i === 0} />
                ))}
              </div>
            ) : null}

            {vm.hasPresentation ? (
              <Section
                title="Appearance on the plan"
                info={`Changes how the row looks on the plan. It never changes the numbers. Now: ${vm.authLookSummary}.`}
                open={lookOpen}
                onToggle={() => setLookOpen(!lookOpen)}
              >
                <div style={sx(`display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px`)}>
                  <Field label="Text">
                    <Chips items={vm.presWeight} busy={busy} />
                  </Field>
                  <Field label="Numbers">
                    <Chips items={vm.presNumWeight} busy={busy} />
                  </Field>
                  <Field label="Indent">
                    <Chips items={vm.presIndent} busy={busy} />
                  </Field>
                </div>
                <Field label="Rounding" info="Decimal places shown for this driver everywhere it appears: plan cells, quarter and year totals, explain cards, the chart, the change log and the Excel export. The model always calculates at full precision. Auto keeps the standard format for the unit.">
                  <Chips items={vm.presRound} busy={busy} />
                </Field>
                <Field label="Driver change direction" info="Colours the Change and Change % values on the plan. Direct shows an increase in green and a decrease in red. Inverse is for drivers where lower is better, such as CAC or cost per session, so an increase shows in red and a decrease in green.">
                  <div role="radiogroup" aria-label="Driver change direction" style={sx(`display:flex;gap:8px;flex-wrap:wrap`)}>
                    {(vm.presDirection || []).map((o) => {
                      const off = !!o.disabled || busy;
                      return (
                        <label
                          key={o.id}
                          style={sx(`flex:none;display:flex;align-items:center;gap:8px;height:32px;padding:0 13px;border:1px solid ${o.checked ? '#0052CD' : '#C8D4EA'};border-radius:999px;background:${o.checked ? '#EAF1FD' : '#fff'};font-size:12px;color:${o.checked ? '#0052CD' : 'var(--ink-3)'};cursor:${busy ? 'wait' : off ? 'not-allowed' : 'pointer'};opacity:${off ? 0.65 : 1}`)}
                        >
                          <input
                            type="radio"
                            name="driver-change-direction"
                            value={o.id}
                            checked={o.checked}
                            disabled={off}
                            onChange={o.onSelect}
                            aria-label={o.label + ' (' + o.hint + ')'}
                            style={sx(`margin:0;accent-color:#0052CD;cursor:inherit`)}
                          />
                          <span style={sx(`font-weight:500`)}>{o.label}</span>
                          <span style={sx(`color:var(--ink-4)`)}>({o.hint})</span>
                          <span aria-hidden="true" style={sx(`display:flex;gap:4px;font-size:11px;font-weight:600;font-variant-numeric:tabular-nums`)}>
                            <span style={sx(`color:${o.upFg}`)}>+12</span>
                            <span style={sx(`color:${o.downFg}`)}>−12</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Background">
                  <div style={sx(`display:flex;gap:7px;flex-wrap:wrap`)}>
                    {(vm.presBg || []).map((o, i) => (
                      <button
                        key={i}
                        onClick={o.onClick}
                        disabled={!!o.disabled || busy}
                        title={o.label}
                        style={sx(`flex:none;display:flex;align-items:center;gap:7px;height:30px;padding:0 12px;border:1px solid ${o.border};border-radius:999px;background:#fff;color:${o.fg};font-family:inherit;font-size:12px;cursor:${o.disabled || busy ? 'wait' : 'pointer'};opacity:${o.disabled || busy ? 0.65 : 1}`)}
                      >
                        <span style={sx(`flex:none;width:12px;height:12px;border-radius:3px;background:${o.swatch};border:1px solid #DCE4F2`)}></span>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </Field>
                <div style={sx(`display:flex;align-items:center;gap:10px;padding:11px 13px;border-radius:8px;background:var(--bg-1)`)}>
                  <div style={sx(`flex:1;min-width:0;font-size:12px;color:var(--ink-2);line-height:1.5`)}>{vm.presVisibleNote}</div>
                  <button
                    onClick={vm.presToggleHide}
                    disabled={busy}
                    style={sx(`flex:none;height:28px;padding:0 12px;border:1px solid #C8D4EA;border-radius:999px;background:#fff;color:var(--ink-2);font-family:inherit;font-size:11.5px;cursor:${busy ? 'wait' : 'pointer'};opacity:${busy ? 0.65 : 1}`)}
                  >
                    {vm.presVisibleLabel}
                  </button>
                </div>
              </Section>
            ) : null}

            <Section title="Checks" info="Automatic checks run on every change. Anything that fails is shown in red above." summary={checksSummary} open={checksOpen} onToggle={() => setChecksOpen(!checksOpen)}>
              <div>
                {passed.map((c, i) => (
                  <CheckItem key={i} c={c} />
                ))}
              </div>
            </Section>
          </div>
        </div>

        <div style={sx(`flex:none;display:flex;align-items:center;gap:12px;padding:14px 24px;border-top:1px solid #DCE4F2;background:#fff`)}>
          <span style={sx(`flex:1;min-width:0;font-size:11.5px;font-weight:400;color:var(--ink-4);line-height:1.5`)}>{vm.authNote}</span>
          <button
            onClick={vm.closeAuth}
            disabled={busy}
            style={sx(`flex:none;height:38px;padding:0 16px;border:1px solid #C8D4EA;border-radius:999px;background:#fff;color:var(--ink-3);font-family:inherit;font-size:13px;cursor:${busy ? 'wait' : 'pointer'};opacity:${busy ? 0.6 : 1}`)}
          >
            Discard
          </button>
          <button
            onClick={vm.authCommit}
            disabled={busy || vm.authBtnBg !== '#0052CD'}
            style={sx(`flex:none;height:38px;padding:0 18px;border:none;border-radius:999px;background:${vm.authBtnBg};color:${vm.authBtnFg};font-family:inherit;font-size:13px;font-weight:500;cursor:${busy ? 'wait' : 'pointer'};opacity:${busy ? 0.75 : 1}`)}
          >
            {vm.authBtnLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AuthoringModal({ vm }: { vm: any }) {
  return vm.authOpen ? <AuthoringDialog vm={vm} /> : null;
}
